import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSignInOpen, setIsSignInOpen] = useState(false);
  const [isSignUpOpen, setIsSignUpOpen] = useState(false);

  // Check for existing session on app load
  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setIsLoading(false);
      return;
    }

    // Check localStorage first for faster UX
    const savedUser = localStorage.getItem('legalconverter_user');
    if (savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch (error) {
        console.error('Error parsing saved user:', error);
        localStorage.removeItem('legalconverter_user');
      }
    }

    // Verify with backend (optional - for session validation)
    try {
      const response = await fetch(`${API}/auth/me`, {
        credentials: 'include',
      });
      
      if (response.ok) {
        const userData = await response.json();
        const user = {
          id: userData.user_id,
          email: userData.email,
          name: userData.name,
          picture: userData.picture,
          subscription: userData.subscription || 'free',
          createdAt: userData.created_at
        };
        setUser(user);
        localStorage.setItem('legalconverter_user', JSON.stringify(user));
      }
    } catch (error) {
      // Silent fail - user might not be logged in
      console.debug('No active session');
    }

    setIsLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const signIn = async (email, password) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Invalid email or password');
      }

      const userData = await response.json();
      
      const user = {
        id: userData.user_id,
        email: userData.email,
        name: userData.name,
        picture: userData.picture,
        subscription: userData.subscription || 'free',
        uploadsRemaining: userData.subscription === 'professional' ? -1 : 3,
        analysesRemaining: userData.subscription === 'professional' ? -1 : 1,
        createdAt: userData.created_at
      };
      
      setUser(user);
      localStorage.setItem('legalconverter_user', JSON.stringify(user));
      setIsSignInOpen(false);
      return { success: true };
    } catch (error) {
      console.error('Sign in error:', error);
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  };

  const signUp = async (name, email, password) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API}/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name, email, password })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create account');
      }

      const userData = await response.json();
      
      const user = {
        id: userData.user_id,
        email: userData.email,
        name: userData.name,
        subscription: 'free',
        uploadsRemaining: 3,
        analysesRemaining: 1,
        createdAt: userData.created_at
      };
      
      setUser(user);
      localStorage.setItem('legalconverter_user', JSON.stringify(user));
      setIsSignUpOpen(false);
      return { success: true };
    } catch (error) {
      console.error('Sign up error:', error);
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  };

  const signOut = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
    localStorage.removeItem('legalconverter_user');
  };

  const updateUserUsage = (uploadsUsed = 0, analysesUsed = 0) => {
    if (user && user.subscription === 'free') {
      const updatedUser = {
        ...user,
        uploadsRemaining: Math.max(0, user.uploadsRemaining - uploadsUsed),
        analysesRemaining: Math.max(0, user.analysesRemaining - analysesUsed)
      };
      setUser(updatedUser);
      localStorage.setItem('legalconverter_user', JSON.stringify(updatedUser));
    }
  };

  const canUpload = () => {
    if (!user) return false;
    if (user.subscription === 'professional') return true;
    return user.uploadsRemaining > 0;
  };

  const canAnalyze = () => {
    if (!user) return false;
    if (user.subscription === 'professional') return true;
    return user.analysesRemaining > 0;
  };

  const value = {
    user,
    setUser,
    isLoading,
    isSignInOpen,
    setIsSignInOpen,
    isSignUpOpen,
    setIsSignUpOpen,
    signIn,
    signUp,
    signOut,
    updateUserUsage,
    canUpload,
    canAnalyze
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};